/**
 * Stamp the chosen interior wood onto the BOM.
 *
 * Lives in the enrichment chain rather than in each engine: all three engines
 * emit the same `materialKey`s for panel and lav rows, so one pass over
 * `result.materials` is the whole job (see the shared-enrichment-chains note).
 *
 * Two things change per row, and they must change together:
 *  - the **benämning** gains `", <species>"` — a Swedish stable ID like every
 *    other engine string, stripped and re-translated by `translateMaterial`;
 *  - the **priceRuleId** moves to that species' rule, so the quote is for the
 *    wood the viewer is showing.
 */
import type { MaterialRow, CutRow } from "../../engines/types";
import {
	INTERIOR_WOOD_BENCH_KEYS,
	INTERIOR_WOOD_CATALOG,
	INTERIOR_WOOD_PANEL_KEYS,
	SPECIES_SUFFIX_RE,
} from "./finishCatalog";
import { DEFAULT_FINISHES, type FinishConfig } from "./types";

/** Drop a suffix this module wrote, so re-applying is a no-op. */
export function stripSpeciesSuffix(materialName: string): string {
	const match = materialName.match(SPECIES_SUFFIX_RE);
	return match ? materialName.slice(0, match.index) : materialName;
}

/**
 * The exact `material` string an interior-wood row ends up with.
 *
 * Exported for the audit's `sourceLabel`s, which match BOM rows BY NAME. If
 * the audit spelled the base name and the BOM spelled the suffixed one, every
 * bench and panel evidence row would silently find zero BOM rows to check
 * itself against — a parity gate that passes by matching nothing.
 */
export function interiorWoodRowLabel(
	baseMaterialName: string,
	finishes: FinishConfig = DEFAULT_FINISHES,
): string {
	return `${stripSpeciesSuffix(baseMaterialName)}, ${
		INTERIOR_WOOD_CATALOG[finishes.interiorWood].bomWordSv
	}`;
}

const PANEL_KEYS = new Set(INTERIOR_WOOD_PANEL_KEYS);
const BENCH_KEYS = new Set(INTERIOR_WOOD_BENCH_KEYS);

/**
 * Return `rows` with every interior-wood row renamed and repriced for
 * `finishes.interiorWood`. Rows without a matching `materialKey` are returned
 * unchanged (same object identity), so this is cheap on the common path.
 */
export function applyInteriorWoodToRows(
	rows: MaterialRow[],
	finishes: FinishConfig = DEFAULT_FINISHES,
): MaterialRow[] {

	return rows.map((row) => {
		const key = row.materialKey;
		if (!key) return row;
		const isPanel = PANEL_KEYS.has(key);
		const isBench = BENCH_KEYS.has(key);
		if (!isPanel && !isBench) return row;

		const spec = INTERIOR_WOOD_CATALOG[isBench ? row.woodSpecies ?? finishes.interiorWood : finishes.interiorWood];
		return {
			...row,
			material: `${stripSpeciesSuffix(row.material)}, ${spec.bomWordSv}`,
			priceRuleId: isPanel ? spec.panelPriceRuleId : spec.benchPriceRuleId,
		};
	});
}

/** Only authored mixed-stock plans need species keys; preserve legacy cut output otherwise. */
export function applyInteriorWoodToCuts(
	rows: CutRow[],
	finishes: FinishConfig = DEFAULT_FINISHES,
): CutRow[] {
	if (!rows.some((row) => row.woodSpecies)) return rows;
	return rows.map((row) => {
		if (!BENCH_KEYS.has(row.materialKey ?? "")) return row;
		const woodSpecies = row.woodSpecies ?? finishes.interiorWood;
		return {
			...row,
			woodSpecies,
			part: `${stripSpeciesSuffix(row.part)}, ${INTERIOR_WOOD_CATALOG[woodSpecies].bomWordSv}`,
		};
	});
}
